import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/utils/apiError";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const done = new Set(["Delivery Complete", "Complete", "Completed", "Delivered"]);
const visibleStatus = status => ({
  "Awaiting Pickup": "Pick-Up Done",
  "Awaiting Completion": "Out for Delivery",
  "Delivery Complete": "Completed",
  "Complete": "Completed",
  "Delivered": "Completed",
}[status] || status);
const cls = "h-10 border border-slate-300 bg-white px-3 text-sm rounded-sm focus:outline-none focus:border-[#1B2A6B]";
export const movementActions = (t, allowComplete = false) => {
  const m = (t.movement_history || []).at(-1);
  if (!m || m.event_version !== 1 || done.has(t.status)) return [];
  if (allowComplete && m.status === "Delivered" && ["Out for Delivery", "Awaiting Completion"].includes(t.status)) return [["complete", "Mark Completed"]];
  if (m.status === "Scheduled") return [["depart", "Confirm Departure"], ["cancel", "Cancel Movement"]];
  if (m.status === "Departed") return m.destination.type === "to_location" ? [["deliver", "Confirm Delivery"]] : [["arrive", "Confirm Arrival"]];
  return [];
};
export async function recordMovement(t, action) {
  const m = t.movement_history.at(-1);
  return (await axios.post(`${API}/consignments/${t.id}/movements/${m.id}/action`, { action }, { withCredentials: true })).data;
}

export function MovementStatusSelect({ transfer, tripId, onSaved, className = "" }) {
  const { user } = useAuth();
  const editable = ["admin", "superadmin", "manager", "supervisor", "warehouse_manager"].includes(user?.role);
  const canComplete = ["superadmin", "warehouse_manager"].includes(user?.role);
  const [busy, setBusy] = useState(false);
  const latest = (transfer.movement_history || []).at(-1);
  const belongsToTrip = !tripId || latest?.trip_id === tripId;
  const actions = belongsToTrip ? movementActions(transfer, canComplete) : [];

  const placeholder = !editable
    ? visibleStatus(transfer.status)
    : !transfer.location_confirmed
      ? "Confirm location first"
      : !belongsToTrip
        ? "Historical movement"
        : actions.length === 0
          ? visibleStatus(transfer.status)
          : "Update movement";

  const handleChange = async event => {
    const action = event.target.value;
    event.target.value = "";
    if (!action) return;
    const label = actions.find(([value]) => value === action)?.[1] || "Update movement";
    if (!window.confirm(`${label} for ${transfer.lr_number}?`)) return;
    setBusy(true);
    try {
      const updated = await recordMovement(transfer, action);
      onSaved?.(updated);
      toast.success(`${transfer.lr_number} updated.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Unable to update consignment movement."));
    } finally {
      setBusy(false);
    }
  };

  return <select
    aria-label={`Update movement for ${transfer.lr_number}`}
    defaultValue=""
    disabled={busy || !editable || actions.length === 0}
    onChange={handleChange}
    className={`h-9 min-w-[165px] border border-slate-300 bg-white px-2 text-xs focus:outline-none focus:border-[#1B2A6B] disabled:bg-slate-50 disabled:text-slate-500 ${className}`}
  >
    <option value="">{busy ? "Updating..." : placeholder}</option>
    {actions.map(([action, label]) => <option key={action} value={action}>{label}</option>)}
  </select>;
}

export default function ConsignmentMovement({ transfer: t, onSaved, tripId }) {
  const { user } = useAuth();
  const editable = ["admin", "superadmin", "manager", "supervisor", "warehouse_manager"].includes(user?.role);
  const canComplete = ["superadmin", "warehouse_manager"].includes(user?.role);
  const [warehouses, setWarehouses] = useState([]);
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState("");
  const [point, setPoint] = useState("from_location");
  const [destination, setDestination] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!editable || tripId) return;
    Promise.all([axios.get(`${API}/warehouses`, { withCredentials: true }), axios.get(`${API}/trips`, { withCredentials: true })])
      .then(([w, tripsRes]) => { setWarehouses(w.data); setTrips(tripsRes.data); })
      .catch(e => toast.error(getApiErrorMessage(e, "Unable to load movement options.")));
  }, [editable, tripId]);
  const asPoint = v => v.startsWith("warehouse:") ? { type: "warehouse", warehouse_id: v.slice(10) } : { type: v };
  const run = async operation => {
    setError("");
    setBusy(true);
    try { const updated = await operation(); onSaved?.(updated); setCorrecting(false); setReason(""); toast.success("Consignment updated."); }
    catch (e) {
      const message = getApiErrorMessage(e, "Unable to update movement.");
      setError(message);
      toast.error(message);
    }
    finally { setBusy(false); }
  };
  const confirmLocation = () => {
    if (!reason.trim()) {
      const message = "Enter a confirmation note before saving the location.";
      setError(message);
      toast.error(message);
      return;
    }
    run(async () => (await axios.post(
      `${API}/consignments/${t.id}/location`,
      { point: asPoint(point), reason: reason.trim() },
      { withCredentials: true },
    )).data);
  };
  const latest = (t.movement_history || []).at(-1);
  const pending = latest?.event_version === 1 && ["Scheduled", "Departed"].includes(latest.status);
  const actions = movementActions(t, canComplete);
  const matchesTrip = !tripId || latest?.trip_id === tripId;
  const options = warehouses.map(w => <option key={w.id} value={`warehouse:${w.id}`}>{w.warehouse_name}</option>);
  return <div className="space-y-3 p-4 border border-slate-200 bg-white">
    <p className="text-sm font-semibold">{t.lr_number} · {visibleStatus(t.status)}</p>
    <p className="text-sm">Current location: {t.current_location || "Unknown"}{!t.location_confirmed && " — needs confirmation"}</p>
    {tripId && <Link className="block text-xs underline" to={`/consignments/${t.id}`}>View LR / manage location and next movement</Link>}
    {tripId && !matchesTrip && <p className="text-xs text-slate-500">Historical trip assignment. The current movement belongs to another trip sheet.</p>}
    {pending && <p className="text-xs text-slate-600">{latest.trip_number} · {latest.status}: {latest.origin.label} → {latest.destination.label}</p>}
    {error && <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">{error}</p>}
    {editable && matchesTrip && actions.length > 0 && <div className="flex flex-wrap gap-2">{actions.map(([action, label]) =>
      <button type="button" key={action} disabled={busy} className={cls} onClick={() => {
        if (window.confirm(`${label} for ${t.lr_number}?`)) run(() => recordMovement(t, action));
      }}>{busy ? "Saving..." : label}</button>)}</div>}
    {editable && !pending && !tripId && <>
      {t.location_confirmed ? (
        <button type="button" className="text-xs font-semibold text-[#1B2A6B] underline" onClick={() => { setError(""); setCorrecting(v => !v); }}>{correcting ? "Cancel location correction" : "Correct location"}</button>
      ) : (
        <p className="text-xs font-bold uppercase tracking-wider text-[#1B2A6B]">Confirm current location</p>
      )}
      {(correcting || !t.location_confirmed) && <div className="grid grid-cols-1 sm:grid-cols-[minmax(220px,1fr)_minmax(220px,1fr)_auto] gap-3 items-end bg-slate-50 border border-slate-200 p-3">
        <label className="space-y-1"><span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600">Actual location</span><select aria-label="Actual location" className={`${cls} w-full`} value={point} onChange={e => { setPoint(e.target.value); setError(""); }}>
          <option value="from_location">Customer pickup location</option><option value="to_location">Customer delivery location</option>{options}
        </select></label>
        <label className="space-y-1"><span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600">Confirmation note *</span><input aria-label="Reason for location confirmation" className={`${cls} w-full`} placeholder="e.g. Physically verified at godown" value={reason} onChange={e => { setReason(e.target.value); setError(""); }} /></label>
        <button type="button" disabled={busy} className={`${cls} bg-[#1B2A6B] text-white font-semibold disabled:opacity-60`} onClick={confirmLocation}>{busy ? "Saving..." : "Confirm Location"}</button>
      </div>}
      {t.location_confirmed && !done.has(t.status) && latest?.status !== "Delivered" && <div className="flex flex-wrap gap-2">
        <select aria-label="Trip sheet" className={cls} value={selectedTrip} onChange={e => setSelectedTrip(e.target.value)}><option value="">Select Trip Sheet</option>
          {trips.filter(tr => !done.has(tr.status) && tr.status !== "Cancelled").map(tr => <option key={tr.id} value={tr.id}>{tr.consignment_number} · {tr.vehicle_display}</option>)}
        </select>
        <select aria-label="Drop point for this LR" className={cls} value={destination} onChange={e => setDestination(e.target.value)}><option value="">Select drop point for this LR</option><option value="to_location">Customer delivery location</option>{options}</select>
        <button type="button" disabled={busy || !selectedTrip || !destination} className={cls} onClick={() => run(async () => (await axios.patch(`${API}/consignments/${t.id}/consignment`, { consignment_id: selectedTrip, destination: asPoint(destination) }, { withCredentials: true })).data)}>Assign Movement</button>
      </div>}
    </>}
  </div>;
}
